import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import styles from './index.module.scss';
import cn from 'classnames';
import { SvgIcon } from '@ui-elements';
import { colorPrimary, toCamelCase } from '~/common/utils';
import { Button, IconEnum } from '~/common/components/ui-elements';
import { TileView, FullWidthView, FullScrollView } from './components';
import { CallToAction } from '~/common/models';
import stylesHeroCard from '../hero-card/index.module.scss';
import { DataHeader, GroupedData } from './models';

type VariantSpecsComparisonType = {
  heading: string;
  disclaimer: string;
  headerDisclaimer?: string;
  headerDisclaimerSizeClass?: string;
  contentCTAs: CallToAction[];
  groupedData: GroupedData[];
  dataHeader: DataHeader[];
};

export const VariantSpecsComparison = React.memo(
  (props: VariantSpecsComparisonType) => {
    return <VariantSpecsComparisonContent {...props} />;
  }
);

const DisclaimerView = (props) => {
  const { disclaimer, dataHeader } = props;

  return (
    <div className={styles.ContentDataContainer}>
      <div
        className={cn(styles.ContentData, {
          [styles.MinimizeColumns]: dataHeader.length < 3,
        })}
      >
        <div />
        <div
          className={styles.ContentDisclaimer}
          dangerouslySetInnerHTML={{ __html: disclaimer }}
        />
      </div>
    </div>
  );
};

const MobileDisclaimerView = (props) => {
  const { disclaimer } = props;
  return (
    <div className={styles.ContentDataContainer}>
      <div
        className={styles.ContentDisclaimer}
        dangerouslySetInnerHTML={{ __html: disclaimer }}
      />
    </div>
  );
};

const VariantSpecsComparisonContent = (props: VariantSpecsComparisonType) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isMobileView, setIsMobileView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (containerRef.current !== null) {
      if (
        containerRef.current?.clientWidth < 391 ||
        containerRef.current?.clientWidth < 769 ||
        containerRef.current?.clientWidth < 1025
      ) {
        setIsMobileView(true);
      } else {
        setIsMobileView(false);
      }
    }
  }, []);

  useEffect(() => {
    if (ref.current) {
      const lastChild = ref.current.lastChild as HTMLElement;
      const parentElement = ref.current.parentElement as HTMLElement;
      parentElement.style.textAlign = lastChild?.style?.textAlign;
    }
  }, [ref.current]);

  return (
    <div
      ref={containerRef}
      className={styles.VariantSpecsComparison}
      style={{ marginBottom: '50px' }}
    >
      <div className={styles.Header}>
        <div
          ref={ref}
          className={styles.HeaderContent}
          dangerouslySetInnerHTML={{ __html: props.heading }}
        />
        {props.headerDisclaimer && (
          <span
            className={cn(
              `tt ${styles.HeaderDisclaimerTrigger} ${
                props.headerDisclaimerSizeClass &&
                props.headerDisclaimerSizeClass !== 'none'
                  ? props.headerDisclaimerSizeClass
                  : styles.Disclaimer
              }`
            )}
            style={{
              color: `#${colorPrimary}`,
            }}
          >
            *
            <span
              className="tt__content"
              dangerouslySetInnerHTML={{
                __html: props.headerDisclaimer as string,
              }}
            />
          </span>
        )}
      </div>
      {!isMobileView && (
        <FullScrollView
          groupedData={props.groupedData}
          dataHeader={props.dataHeader}
        />
      )}
      {isMobileView && (
        <TileView
          groupedData={props.groupedData}
          dataHeader={props.dataHeader}
        />
      )}
      {isMobileView && <MobileDisclaimerView disclaimer={props.disclaimer} />}
      {!isMobileView && (
        <DisclaimerView
          isMobileView={isMobileView}
          disclaimer={props.disclaimer}
          dataHeader={props.dataHeader}
        />
      )}
      <div className={styles.ContentDataContainer}>
        <div className={styles.CTAContainer}>
          {props.contentCTAs?.map((cta, idx) => {
            const buttonProps: any = {
              type: toCamelCase(cta.ctaButtonType),
              href: cta.ctaUrl?.url,
              target: cta.ctaUrl?.target ?? '_self',
              onClick: () => {},
            };
            return (
              <Button
                key={idx}
                className={cn(
                  stylesHeroCard.HeroCardCTA,
                  stylesHeroCard[`HeroCardCTA${buttonProps.type}`]
                )}
                {...buttonProps}
              >
                {cta.ctaIcon && cta.ctaIcon !== 'none' && (
                  <SvgIcon
                    className={styles.CTAIcon}
                    type={cta.ctaIcon as keyof typeof IconEnum}
                    color="#1637a0"
                    size={1.2}
                    strokeWidth={2}
                  />
                )}
                <span
                  className={styles.CTAText}
                  style={
                    buttonProps.type === 'text' ? { color: '#1637a0' } : {}
                  }
                >
                  {cta.ctaUrl?.name || ''}
                </span>
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
